import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

/**
 * Banner global que aparece automáticamente cuando se pierde conexión.
 * Se desliza desde arriba con animación de 300ms.
 *
 * Cuando vuelve la conexión: desaparece automáticamente con un
 * "✅ Conexión restaurada" verde de 2 segundos.
 */
export default function OfflineBanner() {
  const { isOnline } = useNetworkStatus();
  const slide = useRef(new Animated.Value(-60)).current;
  const wasOffline = useRef(false);
  const [showReconnected, setShowReconnected] = React.useState(false);

  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      Animated.timing(slide, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else if (wasOffline.current) {
      // Mostrar "Conexión restaurada" por 2s
      setShowReconnected(true);
      const timer = setTimeout(() => {
        Animated.timing(slide, {
          toValue: -60,
          duration: 300,
          useNativeDriver: true,
        }).start(() => {
          setShowReconnected(false);
          wasOffline.current = false;
        });
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      slide.setValue(-60);
    }
  }, [isOnline, slide]);

  if (isOnline && !showReconnected) return null;

  const offline = !isOnline;

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.bar,
        offline ? styles.offline : styles.online,
        { transform: [{ translateY: slide }] },
      ]}
    >
      <Ionicons
        name={offline ? 'cloud-offline' : 'cloud-done'}
        size={14}
        color="#FFF"
      />
      <Text style={styles.text}>
        {offline
          ? 'Sin conexión — usando datos guardados'
          : 'Conexión restaurada'}
      </Text>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  bar: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 50 : 30,
    left: 16,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 22,
    zIndex: 9999,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 3 },
    shadowRadius: 6,
    elevation: 6,
  },
  offline: { backgroundColor: '#D32F2F' },
  online: { backgroundColor: '#2E7D32' },
  text: {
    color: '#FFF',
    fontSize: 12,
    fontWeight: '700',
    marginLeft: 6,
  },
});
